import React, {createContext, useEffect, useReducer} from 'react';
import UserProfileModel from '~/src/models/UserProfile';
import {getUserProfile} from '~/src/utilities/feed/profile';

enum ActionTypes {
    LOAD_PROFILE_REQUEST,
    LOAD_PROFILE_SUCCESS,
    LOAD_PROFILE_FAILURE,
    LOAD_PROFILE_REFRESH
}

interface UserProfileAction {
    type: ActionTypes;
    payload?: UserProfileModel;
}

interface UserProfileState {
    isLoading: boolean;
    isLoaded: boolean;
    data?: UserProfileModel;
}

interface UserProfileProviderProps {
    cacheKey: string;
    children: React.ReactNode;
}

const DEFAULT_STATE: UserProfileState = {
    isLoading: false,
    isLoaded: false
};

export const UserProfileContext = createContext(DEFAULT_STATE);

type UserProfileReducer = (state: UserProfileState, action: UserProfileAction) => UserProfileState;
const reducer: UserProfileReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.LOAD_PROFILE_REQUEST:
            return {
                ...state,
                isLoading: true,
                isLoaded: false
            };
        case ActionTypes.LOAD_PROFILE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                data: action.payload
            };
        case ActionTypes.LOAD_PROFILE_FAILURE:
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                data: undefined
            };
        case ActionTypes.LOAD_PROFILE_REFRESH:
            return {
                ...state,
                isLoaded: false
            };
        default:
            return state;
    }
};

const UserProfileProvider = ({cacheKey, children}: UserProfileProviderProps) => {
    const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);

    if (process.browser) {
        useEffect(() => {
            if (!state.isLoading) {
                dispatch({type: ActionTypes.LOAD_PROFILE_REQUEST});
            }
        }, [cacheKey]);

        useEffect(() => {
            if (state.isLoading) {
                getUserProfile({forceLogin: false})
                    .then((profile) => {
                        if (!profile) {
                            return Promise.reject();
                        }
                        dispatch({type: ActionTypes.LOAD_PROFILE_SUCCESS, payload: profile});
                    })
                    .catch(() => {
                        dispatch({type: ActionTypes.LOAD_PROFILE_FAILURE});
                    });
            }
        }, [state.isLoading]);
    }

    return <UserProfileContext.Provider value={state}>{children}</UserProfileContext.Provider>;
};

type WithUserProfileContext = <P extends object>(WrappedComponent: React.ComponentType<P>) => React.ElementType;
export const withUserProfileContext: WithUserProfileContext = (WrappedComponent) => ({context, ...props}) => (
    <UserProfileContext.Consumer>{({data: userProfile}) => <WrappedComponent context={{...context, userProfile}} {...props} />}</UserProfileContext.Consumer>
);

export default UserProfileProvider;
